export type HighlightStripState = {
  startIndex: number;
  fullSlots: 1 | 2;
  peekLeft: boolean;
  peekRight: boolean;
  showPrev: boolean;
  showNext: boolean;
};

/** Newest published card first; same time keeps the id ascending. */
export function sortHighlightItems<T extends { id: string; publishedAt: string | null }>(
  items: readonly T[],
): T[] {
  return [...items].sort((left, right) => {
    const leftAt = left.publishedAt ? Date.parse(left.publishedAt) : 0;
    const rightAt = right.publishedAt ? Date.parse(right.publishedAt) : 0;
    if (leftAt !== rightAt) {
      return rightAt - leftAt;
    }
    return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
  });
}

/** Desktop: two full cards. Below md: one. Right peek while more remain; left peek at the end. */
export function highlightStripState(
  count: number,
  startIndex: number,
  desktop: boolean,
): HighlightStripState {
  const fullSlots: 1 | 2 = desktop ? 2 : 1;
  const maxStart = Math.max(0, count - fullSlots);
  const start = Math.min(Math.max(0, startIndex), maxStart);
  const peekRight = count - start > fullSlots;
  const peekLeft = start > 0 && !peekRight;
  return {
    startIndex: start,
    fullSlots,
    peekLeft,
    peekRight,
    showPrev: start > 0,
    showNext: peekRight,
  };
}

const STRIP_GAP = '0.75rem';

export type HighlightStripMetrics = {
  cardWidth: string;
  peekWidth: string | null;
};

/** Card size is always two-and-a-quarter slots (desktop) or one-and-a-quarter (below md). */
export function highlightStripMetrics(fullSlots: 1 | 2): HighlightStripMetrics {
  const cardWidth = `calc((100% - ${fullSlots} * ${STRIP_GAP}) / ${fullSlots + 0.25})`;
  return {
    cardWidth,
    peekWidth: `calc((100% - ${fullSlots} * ${STRIP_GAP}) / ${fullSlots + 0.25} * 0.25)`,
  };
}

export function highlightStripTrackShift(
  startIndex: number,
  peekLeft: boolean,
  cardWidth: string,
  peekWidth: string,
): string {
  const step = `(${cardWidth} + ${STRIP_GAP})`;
  if (peekLeft) {
    return `calc(-1 * ${startIndex} * ${step} + ${peekWidth} + ${STRIP_GAP})`;
  }
  return `calc(-1 * ${startIndex} * ${step})`;
}

const SNAP_DISTANCE = 0.28;
const SNAP_FLICK = 0.35;
const DRAG_RESISTANCE = 0.32;

/** Soften the finger offset when the strip is already at an end. */
export function highlightStripDragPx(dragPx: number, startIndex: number, maxStart: number): number {
  if ((startIndex <= 0 && dragPx > 0) || (startIndex >= maxStart && dragPx < 0)) {
    return dragPx * DRAG_RESISTANCE;
  }
  return dragPx;
}

export function highlightStripSnap({
  startIndex,
  maxStart,
  dragPx,
  stepPx,
  velocityPxPerMs,
}: {
  startIndex: number;
  maxStart: number;
  dragPx: number;
  stepPx: number;
  velocityPxPerMs: number;
}): number {
  const threshold = stepPx > 0 ? stepPx * SNAP_DISTANCE : 24;
  let next = startIndex;
  if (Math.abs(velocityPxPerMs) >= SNAP_FLICK) {
    next = velocityPxPerMs > 0 ? startIndex - 1 : startIndex + 1;
  } else if (Math.abs(dragPx) >= threshold) {
    next = dragPx > 0 ? startIndex - 1 : startIndex + 1;
  }
  return Math.min(maxStart, Math.max(0, next));
}
